import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';

import { isWrappedMasterKeyEnrolled } from '../../services/encryption/MasterKeyService';
import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';

import lockIcon from '../../assets/icons/lock.png';

const logger = createLogger('RecoveryCodeSettings');

interface Props {
  navigation: NavigationProp<any>;
}

/**
 * Hub for recovery-code management. Two visible states:
 *
 *   1. No code set — primary CTA "Set up recovery code" routes to the
 *      intro screen.
 *   2. Code set — shows "Recovery code is set" badge, offers "Change
 *      recovery code" secondary.
 *
 * Re-checks on focus so a just-completed enrollment is reflected
 * immediately when the user hits the back stack.
 */
const RecoveryCodeSettingsScreen: React.FC<Props> = ({ navigation }) => {
  const [enrolled, setEnrolled] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    try {
      const isSet = await isWrappedMasterKeyEnrolled();
      setEnrolled(isSet);
    } catch (err) {
      logger.error('Failed to check enrollment status');
      // Treat unknown as "not set" so the user can still try to enroll.
      setEnrolled(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void refresh();
    }, [refresh]),
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Recovery Code</Text>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.badge}>
          <Image source={lockIcon} style={styles.badgeIcon} />
        </View>

        <Text style={styles.title}>
          Your recovery code unlocks your vault on a new device
        </Text>

        <Text style={styles.description}>
          When set, your vault is encrypted with a key only you know. You'll need
          this code if you ever sign in on a different phone, or if you need to
          recover access to your account.
        </Text>

        <View style={styles.warningBox}>
          <Text style={styles.warningTitle}>⚠ Important</Text>
          <Text style={styles.warningBody}>
            If you forget your recovery code, nothing we can do on our end will
            get your vault back. Write it down somewhere safe — a password
            manager, a piece of paper in a locked drawer, whatever you trust.
          </Text>
        </View>

        {enrolled === null ? (
          <View style={styles.stateContainer}>
            <ActivityIndicator size="small" color="#29BFC0" />
          </View>
        ) : enrolled ? (
          <View style={styles.setContainer}>
            <Text style={styles.setLabel}>Recovery code is set</Text>
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={() => navigation.navigate('ChangeRecoveryCode')}
              accessibilityRole="button"
            >
              <Text style={styles.secondaryButtonText}>Change recovery code</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.setContainer}>
            {/* Owner decision 2026-10-05: accounts without a code are allowed,
                and the product says plainly what that means. */}
            <Text style={styles.noCodeNotice}>
              Without a recovery code, FinalKey's server holds the key to your
              documents and saved logins, so our staff or anyone who broke into
              our systems could read them. With a code, only you can.
            </Text>
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={() => navigation.navigate('SetRecoveryCodeIntro')}
              accessibilityRole="button"
            >
              <Text style={styles.primaryButtonText}>Set up recovery code</Text>
            </TouchableOpacity>
          </View>
        )}

        <TouchableOpacity
          style={styles.dashboardButton}
          onPress={() => navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] })}
          accessibilityRole="button"
          accessibilityLabel="Back To Your Dashboard"
          accessibilityHint="Return to the dashboard"
          testID="recovery-code-back-to-dashboard"
        >
          <Text style={styles.dashboardButtonText}>Back To Your Dashboard</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: s(16),
    paddingVertical: vs(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  headerTitle: {
    fontSize: ms(16),
    fontWeight: '600',
    color: '#1A1A1A',
  },
  content: {
    padding: s(20),
    alignItems: 'center',
  },
  badge: {
    width: ms(72),
    height: ms(72),
    borderRadius: ms(36),
    backgroundColor: '#E5F7F7',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: vs(16),
    marginBottom: vs(16),
  },
  badgeIcon: {
    width: ms(32),
    height: ms(32),
    tintColor: '#29BFC0',
  },
  title: {
    fontSize: ms(18),
    fontWeight: '700',
    color: '#1A1A1A',
    textAlign: 'center',
    marginBottom: vs(12),
  },
  description: {
    fontSize: ms(14),
    color: '#5A5A5A',
    lineHeight: ms(22),
    textAlign: 'center',
    marginBottom: vs(20),
  },
  warningBox: {
    backgroundColor: '#FFF4E0',
    borderWidth: 1,
    borderColor: '#E6A23C',
    borderRadius: ms(8),
    padding: ms(14),
    width: '100%',
    marginBottom: vs(24),
  },
  warningTitle: {
    color: '#92400E',
    fontWeight: '600',
    fontSize: ms(14),
    marginBottom: vs(4),
  },
  warningBody: {
    color: '#5A3F0F',
    fontSize: ms(13),
    lineHeight: ms(20),
  },
  stateContainer: {
    paddingVertical: vs(20),
  },
  setContainer: {
    alignItems: 'center',
    width: '100%',
  },
  noCodeNotice: {
    fontSize: ms(14),
    lineHeight: ms(21),
    color: '#4A5568',
    textAlign: 'center',
    marginBottom: vs(16),
    paddingHorizontal: s(8),
  },
  setLabel: {
    fontSize: ms(15),
    fontWeight: '600',
    color: '#29BFC0',
    marginBottom: vs(12),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: vs(14),
    paddingHorizontal: s(40),
    borderRadius: ms(8),
    minWidth: ms(220),
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16),
    fontWeight: '600',
  },
  secondaryButton: {
    paddingVertical: vs(12),
    paddingHorizontal: s(24),
  },
  secondaryButtonText: {
    color: '#29BFC0',
    fontSize: ms(14),
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  // Owner request 2026-09-07: the screen's way back is this button, not a
  // header arrow. Accent orange per docs/DESIGN_SYSTEM.md.
  dashboardButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    width: '100%',
    marginTop: vs(28),
  },
  dashboardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
});

export default RecoveryCodeSettingsScreen;
